import { createAsyncThunk } from "@reduxjs/toolkit";

import Cookies from "js-cookie";

import { AppState } from "@/redux/store/store";
import { createCookie } from "@/lib";
import {
  getUser,
  verifyUser,
  getUnverifiedUserToken,
  getRegisteredUserToken,
  changePassword,
  changeProfile,
  logOutUser
} from "@/models";
import { signinUser } from "@/helpers/signin-user";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

export const createUnverifiedUser = createAsyncThunk(
  "user/createUnverifiedUser",
  async (params: { projectId: string }, { rejectWithValue, getState }) => {
    const { projectId } = params;
    const { adapter, user, site } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { email, login } = user.unverifiedUser;
    const { siteName, toolId, toolType } = user.pageData;
    const { name } = site.site;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getUnverifiedUserToken(token, baseUrl, projectId, {
        login,
        email,
        toolId,
        toolType
      });

      const visitedSite = siteName || name;
      response.token &&
        createCookie(`${visitedSite}-unverifiedUserToken`, response.token, 1);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginRegisteredUser = createAsyncThunk(
  "user/loginRegisteredUser",
  async (_, { rejectWithValue, getState }) => {
    const { adapter, questions } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getRegisteredUserToken(token, baseUrl, {
        login: questions.answers["emailOrScreenName"],
        password: questions.answers["loginPassword"]
      });

      if (
        response.token &&
        process.env.NODE_ENV === "development" &&
        !Cookies.get("unexpected_thunder")
      ) {
        Cookies.set("unexpected_thunder", response.token, {
          expires: 2,
          secure: true,
          sameSite: "Strict"
        });
      }

      if (response.token) {
        signinUser(
          questions.answers["emailOrScreenName"],
          questions.answers["loginPassword"],
          "1"
        );
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUser = createAsyncThunk(
  "user/fetchUser",
  async (
    params: Partial<{ config?: object }>,
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await getUser(token, baseUrl, "current", params?.config);

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const validateUser = createAsyncThunk(
  "user/validateUser",
  async (_, { rejectWithValue, getState }) => {
    const { adapter, questions } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const { token } = fetchAuthToken(getState());

    try {
      const response = await verifyUser(token, baseUrl, {
        login: questions.answers["emailOrScreenName"],
        password: questions.answers["loginPassword"]
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "user/updatePassword",
  async (_, { rejectWithValue, getState }) => {
    const { adapter, questions } = getState() as AppState;
    const { token } = fetchAuthToken(getState());
    const { baseUrl } = adapter.adapter;

    try {
      const response = await changePassword(token, baseUrl, {
        oldPassword: questions.answers["oldPassword"],
        newPassword: questions.answers["newPassword"],
        newPasswordConfirmation: questions.answers["newPasswordConfirmation"]
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateProfile = createAsyncThunk(
  "user/updateProfile",
  async (_, { rejectWithValue, getState }) => {
    const { adapter, user } = getState() as AppState;
    const { profile } = user.registeredUser;
    const { token } = fetchAuthToken(getState());
    const { baseUrl } = adapter.adapter;

    try {
      const response = await changeProfile(token, baseUrl, profile?.id, {
        ...profile
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const signOutUser = createAsyncThunk(
  "user/signOutUser",
  async (_, { rejectWithValue, getState }) => {
    const { adapter } = getState() as AppState;
    const { token } = fetchAuthToken(getState());
    const { baseUrl } = adapter.adapter;

    let response;

    try {
      if (process.env.NODE_ENV === "development") {
        response = await logOutUser(token, baseUrl);
        return response;
      } else {
        response = await fetch("/admin/api/auth/logout", {
          method: "DELETE",
          credentials: "same-origin"
        });
      }

      Cookies.remove("unexpected_thunder");
      localStorage.removeItem("UNEXPECTED-THUNDER");

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
